import type { ReactElement } from 'react';
import type { MasteryVisualLevel } from '@/lib/mastery';

type Descriptor = { label: string; filled: 1 | 2 | 3 | 4; tone: 'neutral' | 'warn' | 'brand' | 'ok' };

const DESCRIPTORS: Readonly<Record<MasteryVisualLevel, Descriptor>> = {
  new: { label: 'New', filled: 1, tone: 'neutral' },
  learning: { label: 'Learning', filled: 2, tone: 'warn' },
  solid: { label: 'Solid', filled: 3, tone: 'brand' },
  fluent: { label: 'Fluent', filled: 4, tone: 'ok' },
};

const TONE_COLOR: Readonly<Record<Descriptor['tone'], string>> = {
  neutral: 'var(--ink-4)',
  warn: 'var(--warn)',
  brand: 'var(--brand)',
  ok: 'var(--ok)',
};

export type MasteryMeterProps = {
  level: MasteryVisualLevel;
  showLabel?: boolean;
  size?: 'sm' | 'md';
};

export function MasteryMeter({ level, showLabel = true, size = 'md' }: MasteryMeterProps): ReactElement {
  const d = DESCRIPTORS[level];
  const color = TONE_COLOR[d.tone];
  const dotSize = size === 'sm' ? 6 : 7;
  return (
    <div className="inline-flex items-center gap-1.5">
      <div className="flex items-center gap-1">
        {[0, 1, 2, 3].map((i) => {
          const isFilled = i < d.filled;
          return (
            <div
              key={i}
              data-meter-dot={isFilled ? 'filled' : 'empty'}
              className="rounded-full"
              style={{
                width: dotSize,
                height: dotSize,
                background: isFilled ? color : 'var(--rule-strong)',
              }}
            />
          );
        })}
      </div>
      {showLabel && <span className="text-[11px] font-medium text-[var(--ink-2)]">{d.label}</span>}
    </div>
  );
}
